/**
 * ThinkingSteps — exibe os estágios internos do pipeline de resposta.
 *
 * Modo expandido (collapsed=false): cada step como linha com ícone + texto.
 * Modo colapsado (collapsed=true):  chips compactos mostrando apenas os steps concluídos.
 * O componente colapsa automaticamente quando o primeiro chunk de resposta chega.
 */

import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, CheckCircle2 } from 'lucide-react'

export interface ThinkingStep {
  step: string
  text: string
  status: 'running' | 'done'
}

interface Props {
  steps: ThinkingStep[]
  collapsed: boolean
}

export function ThinkingSteps({ steps, collapsed }: Props) {
  if (steps.length === 0) return null

  if (collapsed) {
    const doneSteps = steps.filter((s) => s.status === 'done')
    if (doneSteps.length === 0) return null
    return (
      <div className="ml-9 flex flex-wrap gap-1 mb-1.5">
        {doneSteps.map((s) => (
          <span
            key={s.step}
            className="inline-flex items-center gap-1 text-[10px] text-muted-foreground border border-border/50 rounded-full px-2 py-0.5 bg-muted/30"
          >
            <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500 shrink-0" />
            {s.text}
          </span>
        ))}
      </div>
    )
  }

  return (
    <AnimatePresence mode="sync">
      <motion.div
        key="thinking-expanded"
        className="ml-9 flex flex-col gap-1 mb-2"
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.15 }}
      >
        {steps.map((s) => (
          <motion.div
            key={s.step}
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.12 }}
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            {s.status === 'running' ? (
              <Loader2 className="h-3 w-3 animate-spin text-primary/60 shrink-0" />
            ) : (
              <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
            )}
            <span>{s.text}</span>
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>
  )
}
